import { useEffect, useState } from 'react'
import Joyride, { CallBackProps, STATUS, Step, EVENTS } from 'react-joyride'
import { useAdminStore } from '@/store/adminStore'
import { useTranslation } from 'react-i18next'

export function TimetableViewTour() {
  const { viewTour, completeViewTour, nextViewStep, setViewStep, resetViewTour } = useAdminStore()
  const [, setStepIndex] = useState(0)
  const { t } = useTranslation('tour')

  const [isRunning, setIsRunning] = useState(false)
  // Start tour when viewTour.isActive changes
  useEffect(() => {
// console.log('TimetableViewTour useEffect:', { 
    //   isActive: viewTour.isActive, 
    //   isRunning, 
    //   hasCompleted: viewTour.hasCompleted 
    // })
    
    if (viewTour.isActive && !isRunning && !viewTour.hasCompleted) {
  // console.logTimetableViewTour: Starting tour...')
      setIsRunning(true)
    } else if (!viewTour.isActive && isRunning) {
  // console.logTimetableViewTour: Tour not active, stopping...')
      setIsRunning(false)
    }
  }, [viewTour.isActive, isRunning, viewTour.hasCompleted])
  const steps: Step[] = [
    {
      target: 'body',
      content: t('admin.timetableViewTour.steps.welcome.content'),
      disableBeacon: true,
      placement: 'center',
    },
    {
      target: '[data-tour="unscheduled-area"]',
      content: t('admin.timetableViewTour.steps.unscheduledArea.content'),
      disableBeacon: true,
      placement: 'right',
      spotlightPadding: 5,
      floaterProps: {
        disableAnimation: true
      }
    },
    {
      target: '[data-tour="class-selection"]',
      content: t('admin.timetableViewTour.steps.classSelection.content'),
      placement: 'bottom',
      spotlightPadding: 5,
      floaterProps: {
        disableAnimation: true
      }
    },
    {
      target: '[data-tour="timetable-grid"]',
      content: t('admin.timetableViewTour.steps.timetableGrid.content'),
      placement: 'left-start',
      spotlightPadding: 5,
      floaterProps: {
        disableAnimation: true
      }
    },
    {
      target: '[data-tour="analyze-button"]',
      content: t('admin.timetableViewTour.steps.analyzeButton.content'),
      placement: 'top',
      spotlightPadding: 5,
      floaterProps: {
        disableAnimation: true
      }
    },
    {
      target: '[data-tour="check-table-button"]',
      content: t('admin.timetableViewTour.steps.checkTableButton.content'),
      placement: 'top',
      spotlightPadding: 5,
      floaterProps: {
        disableAnimation: true
      }
    },
    {
      target: '[data-tour="save-button"]',
      content: t('admin.timetableViewTour.steps.saveButton.content'),
      placement: 'top',
      spotlightPadding: 5,
      floaterProps: {
        disableAnimation: true
      }
    },
    {
      target: '[data-tour="export-button"]',
      content: t('admin.timetableViewTour.steps.exportButton.content'),
      placement: 'left',
      spotlightPadding: 5,
      floaterProps: {
        disableAnimation: true
      }
    },
  ]

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data

    // Handle step changes
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + 1)
      nextViewStep()
    }

    // Handle step before to sync indexes
    if (type === EVENTS.STEP_BEFORE) {
      setStepIndex(index)
      setViewStep(index)
    }

    // Handle tour completion
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED]
    if (finishedStatuses.includes(status as string)) {
      setIsRunning(false)
      completeViewTour()
      
      // Mark the tour as completed in localStorage
      localStorage.setItem('timetableViewTourCompleted', 'true')
  // console.logTimetable view tour completed and saved to localStorage')
    }

    // Handle tour reset
    if (action === 'reset' || action === 'close') {
      resetViewTour()
    }
  }

  // Start at first step when tour becomes active
  useEffect(() => {
    if (viewTour.isActive) {
      setStepIndex(0)
      setViewStep(0)
    }
  }, [viewTour.isActive, setViewStep])

  // Only render if tour is active
  if (!viewTour.isActive) {
// console.logTimetableViewTour: Not rendering - tour not active')
    return null
  }

  // console.log('TimetableViewTour: Rendering tour, isRunning:', isRunning)

  return (
    <>
    <Joyride
    disableOverlayClose={true}
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton
      hideBackButton
      scrollToFirstStep
      showProgress
      showSkipButton
      steps={steps}
      run={isRunning}
      stepIndex={viewTour.currentStep}
      styles={{
        options: {
          primaryColor: '#ffffff',
          zIndex: 10000,
          arrowColor: '#ffffff',
          backgroundColor: '#ffffff',
        },
        buttonNext: {
          backgroundColor: '#ffffff',
          color: '#525FE1',
          fontSize: '16px',
          padding: '10px 20px',
          borderRadius: '6px',
        },
        buttonBack: {
          color: '#666',
          fontSize: '16px',
          padding: '10px',
          marginRight: '12px',
        },
        buttonSkip: {
          color: '#666',
          fontSize: '16px',
          padding: '10px',
        },
        tooltip: {
          padding: '24px',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          color: '#333',
          fontSize: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: '500px',
          minWidth: '300px',
          pointerEvents: 'auto' as const,
          transition: 'opacity 0.2s ease',
        },
        tooltipContainer: {
          textAlign: 'left' as const,
        },
        spotlight: {
          backgroundColor: 'transparent', // Completely transparent
          border: '2px solid white', // White border
          borderRadius: '8px',
          boxShadow: 'none', // Remove box shadow since we handle it in CSS
        },
      }}
      locale={{
        back: t('admin.timetableViewTour.locale.back'),
        close: t('admin.timetableViewTour.locale.close'),
        last: t('admin.timetableViewTour.locale.last'),
        next: t('admin.timetableViewTour.locale.next'),
        skip: t('admin.timetableViewTour.locale.skip'),
      }}
    />
    
    {/* Dark overlay for consistent darkening */}
     {isRunning && viewTour.currentStep === 0 && (
      <div 
        className="fixed inset-0 bg-black/70 z-[9999] pointer-events-none"
        style={{ transition: 'opacity 0.2s ease' }}
      />
    )}
  </>
  )
} 